import Service, { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { ApplicationChangeset } from 'frontend/changesets/application';
import type ApplicationModel from 'frontend/models/application';
import type FetchService from '@triptyk/ember-utils/services/fetch';
import type CurrentUserService from '../current-user';
import type ActionModel from 'frontend/models/action';
import type { ProfileChangeset } from 'frontend/changesets/profile';
import type ExtendedStoreService from '@triptyk/ember-utils/services/extended-store';
import { ProductType } from 'common/enums/product-type.enum';
import { UserKind } from 'common/enums/user-kind.enum';

export default class ApplicationChangesetService extends Service {
  @service declare store: StoreService;
  @service declare extendedStore: ExtendedStoreService;
  @service declare fetch: FetchService;
  @service declare currentUser: CurrentUserService;

  async changesetFromAction(action: ActionModel) {
    const user = this.currentUser.user;
    const isParcoursProduct =
      (await action.product).type === ProductType.Parcours;
    return new ApplicationChangeset({
      kind: user?.kind ?? (isParcoursProduct ? UserKind.Unemployed : undefined),
      newsAgreement: false,
      privacyPolicyAgreement: false,
      termsAgreement: false,
      applicant: {
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        email: user?.email ?? '',
        phone: user?.phone ?? '',
        registerNumber: user?.registerNumber ?? '',
        companyName: user?.companyName ?? '',
        companyNumber: user?.companyNumber ?? '',
        institution: user?.institution ?? '',
        cooperationAgreement: user?.cooperationAgreement ?? false,
      },
      action,
    });
  }

  async save(changeset: ApplicationChangeset): Promise<ApplicationModel> {
    changeset.execute();

    const body = {
      kind: changeset.get('kind'),
      firstName: changeset.get('applicant.firstName'),
      lastName: changeset.get('applicant.lastName'),
      email: changeset.get('applicant.email'),
      phone: changeset.get('applicant.phone'),
      registerNumber: changeset.get('applicant.registerNumber'),
      companyName: changeset.get('applicant.companyName'),
      companyNumber: changeset.get('applicant.companyNumber'),
      institution: changeset.get('applicant.institution'),
      cooperationAgreement: changeset.get('applicant.cooperationAgreement'),
      newsAgreement: changeset.get('newsAgreement'),
      privacyPolicyAgreement: changeset.get('privacyPolicyAgreement'),
      termsAgreement: changeset.get('termsAgreement'),
      action: changeset.get('action'),
      ammonReference: changeset.get('action').code,
    };
    const application: ApplicationModel = this.store.createRecord(
      'application',
      body
    );
    await application.save();
    await changeset.save();

    return application;
  }

  async validate(
    changeset: ProfileChangeset,
    application: ApplicationModel
  ): Promise<ApplicationModel> {
    changeset.execute();

    application.setProperties({
      kind: changeset.data.kind,
      newsAgreement: changeset.data.newsAgreement,
      firstName: changeset.data.applicant.firstName,
      lastName: changeset.data.applicant.lastName,
      email: changeset.data.applicant.email,
      phone: changeset.data.applicant.phone,
      companyNumber: changeset.data.applicant.companyNumber,
      companyName: changeset.data.applicant.companyName,
      institution: changeset.data.applicant.institution,
      cooperationAgreement: changeset.data.applicant.cooperationAgreement,
      gender: changeset.data.gender,
      nationality: changeset.data.nationality,
      professionalSituation: changeset.data.professionalSituation,
      unemployedLength: changeset.data.unemployedLength,
      studyLevel: changeset.data.studyLevel,
      address: {
        streetAndNumber: changeset.data.address?.streetAndNumber,
        zip: changeset.data.address?.zip,
        town: changeset.data.address?.town,
      },
      isBillingAddress: changeset.data.isBillingAddress,
      billingAddress: {
        streetAndNumber: changeset.data.billingAddress?.streetAndNumber,
        zip: changeset.data.billingAddress?.zip,
        town: changeset.data.billingAddress?.town,
      },
      isCommittee: changeset.data.isCommittee,
      isTrainingVoucher: changeset.data.isTrainingVoucher,
      function: changeset.data.function,
      jobStatus: changeset.data.jobStatus,
      educationFunction: changeset.data.educationFunction,
      faseNumber: changeset.data.faseNumber,
      typeOfTeaching: changeset.data.typeOfTeaching,
      isValidated: true,
    });

    await application.save();
    await changeset.save();

    return application;
  }
}

declare module '@ember/service' {
  interface Registry {
    'changesets/application': ApplicationChangesetService;
  }
}
